import { ContentCopy, Done } from '@mui/icons-material';
import { Box, Button as MuiButton, TextField, Typography } from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import Button from '@src/App/Components/Buttons/Button/Button';
import Dialog from '@src/App/Components/Dialogs/Dialog/Dialog';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { ITemplate } from './types';

interface ExportTemplateDialogProps {
    id: string;
    template: ITemplate;
    resolve?: () => void;
}

export const ExportTemplateDialog: React.FC<ExportTemplateDialogProps> = ({ id, template, resolve }) => {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;

    const [jsonString, setJsonString] = useState('');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        // Erstelle eine Kopie des Templates für den Export
        const templateToExport = {
            ...template,
            exportDate: new Date().toISOString(),
        };

        // Konvertiere zu JSON
        setJsonString(JSON.stringify(templateToExport, null, 2));
    }, [template]);

    const handleCopyToClipboard = () => {
        navigator.clipboard.writeText(jsonString).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Dialog
            id={id}
            title={langStrings.exportTemplate}
            footer={
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <MuiButton
                        variant="contained"
                        color="primary"
                        startIcon={copied ? <Done /> : <ContentCopy />}
                        onClick={handleCopyToClipboard}
                    >
                        {copied ? langStrings.copied : langStrings.copyToClipboard}
                    </MuiButton>
                    <Button text={langStrings.close} onClick={() => resolve && resolve()} />
                </Box>
            }
            onClose={() => resolve && resolve()}
        >
            <Box sx={{ width: '100%' }}>
                <Typography variant="body1" gutterBottom>
                    {langStrings.exportTemplateDescription}
                </Typography>
                <TextField
                    multiline
                    fullWidth
                    rows={15}
                    value={jsonString}
                    variant="outlined"
                    InputProps={{
                        readOnly: true,
                    }}
                    sx={{ fontFamily: 'monospace', mt: 2 }}
                />
            </Box>
        </Dialog>
    );
};

export default ExportTemplateDialog;
